import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { useNotesRedirect } from "@/features/NotesManager/hooks";
import {
  allLayoutsSelector,
  allWidgetsSelector,
  deleteWidgetById,
  setAllLayouts,
  setAllWidgets,
} from "../../../slice";
import { ControlSidebar } from "../../ControlSidebar";
import { getWidgetContent, GRID_MAX_COLS_COUNT } from "../../../constants";
import { GridItem } from "../../GridItem";
import styles from "../styles.module.scss";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

export const GridDesktop = () => {
  const { redirect: noteRedirect } = useNotesRedirect();
  const dispatch = useAppDispatch();
  const layouts = useAppSelector(allLayoutsSelector);
  const widgets = useAppSelector(allWidgetsSelector);

  const itemRefs = useRef<HTMLDivElement[]>([]);
  const [containerHeights, setContainerHeights] = useState<number[]>([]);

  useEffect(() => {
    const heights = itemRefs.current.map((ref) => (ref ? ref.clientHeight : 0));
    setContainerHeights(heights);
  }, [layouts]);

  const handleModify = (
    currentLayouts: ReactGridLayout.Layout[],
    layout: ReactGridLayout.Layouts,
  ) => {
    const layoutCopy = JSON.parse(JSON.stringify(layout));
    dispatch(setAllLayouts(layoutCopy));

    const updatedWidgets = widgets.map((widget) => {
      const position = currentLayouts.find((item) => item.i === widget.id);

      return position
        ? { ...widget, x: position.x, y: position.y, w: position.w, h: position.h }
        : widget;
    });

    dispatch(setAllWidgets(updatedWidgets));
  };

  const handleDelete = (key: string) => {
    dispatch(deleteWidgetById(key));
  };

  return (
    <div className={styles.root}>
      <div>
        <ControlSidebar />
      </div>
      <ResponsiveReactGridLayout
        className={styles["grid-layout"]}
        draggableHandle="#drag-handle"
        onLayoutChange={handleModify}
        rowHeight={10}
        resizeHandles={["e", "n", "ne", "nw", "s", "se", "sw", "w"]}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        preventCollision={false}
        cols={{
          lg: GRID_MAX_COLS_COUNT,
          md: GRID_MAX_COLS_COUNT - 27,
          sm: GRID_MAX_COLS_COUNT - 33,
          xs: GRID_MAX_COLS_COUNT - 80,
          xxs: GRID_MAX_COLS_COUNT - 80,
        }}
        autoSize={true}
        margin={{
          lg: [20, 20],
          md: [20, 20],
          sm: [20, 20],
          xs: [20, 20],
          xxs: [20, 20],
        }}
        {...(layouts && { layouts: layouts })}
      >
        {widgets?.map((widget, index) => {
          const currentWidget = getWidgetContent({
            widgetContentId: widget.widgetContentId,
            containerHeight: containerHeights[index],
            onNotesTableRecordClick: (note) => noteRedirect({ note }),
          });
          return (
            <div
              className="reactGridItem"
              key={widget.id}
              data-grid={{
                x: widget?.x,
                y: widget?.y,
                w: widget?.w,
                h: widget?.h,
                i: widget.id,
                minW: currentWidget.minW,
                maxW: currentWidget.maxW,
                minH: currentWidget.minH,
                maxH: currentWidget.maxH,
                isDraggable: true,
                isResizable: true,
              }}
            >
              <GridItem
                currentWidget={currentWidget}
                handleDelete={handleDelete}
                index={index}
                itemRefs={itemRefs}
                widget={widget}
              />
            </div>
          );
        })}
      </ResponsiveReactGridLayout>
    </div>
  );
};
